import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useApi } from '@/hooks/useApi';
import { Loader2 } from 'lucide-react';

interface AddTenantModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const INITIAL_FORM = {
  subdomain: '',
  name_ar: '',
  name_en: '',
  phone: '',
  email: '',
  contact_person: '',
  country_code: 'SD',
  delivery_mode: 'self' as 'self' | 'QuBox' | 'hybrid',
};

export function AddTenantModal({ open, onClose, onSuccess }: AddTenantModalProps) {
  const { loading, post } = useApi({ showSuccessToast: true, showErrorToast: true });
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.subdomain.trim()) newErrors.subdomain = 'النطاق الفرعي مطلوب';
    if (!formData.name_ar.trim()) newErrors.name_ar = 'الاسم بالعربية مطلوب';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'البريد الإلكتروني غير صالح';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      subdomain: formData.subdomain.trim(),
      name_translations: {
        ar: formData.name_ar.trim(),
        en: formData.name_en.trim() || formData.name_ar.trim(),
      },
      contact_info: {
        phone: formData.phone || undefined,
        email: formData.email || undefined,
        contact_person: formData.contact_person || undefined,
      },
      country_code: formData.country_code,
      delivery_mode: formData.delivery_mode,
    };

    const response = await post('/api/v1/tenants', payload);
    if (response?.success) {
      onSuccess();
      onClose();
      setFormData(INITIAL_FORM);
      setErrors({});
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>إضافة شريك جديد</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Subdomain */}
          <div className="space-y-2">
            <Label htmlFor="subdomain">النطاق الفرعي *</Label>
            <Input
              id="subdomain"
              value={formData.subdomain}
              onChange={(e) => setFormData((p) => ({ ...p, subdomain: e.target.value }))}
              placeholder="my-store"
              dir="ltr"
            />
            {errors.subdomain && <p className="text-sm text-destructive">{errors.subdomain}</p>}
          </div>

          {/* Names */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name_ar">الاسم بالعربية *</Label>
              <Input
                id="name_ar"
                value={formData.name_ar}
                onChange={(e) => setFormData((p) => ({ ...p, name_ar: e.target.value }))}
                placeholder="اسم المتجر"
              />
              {errors.name_ar && <p className="text-sm text-destructive">{errors.name_ar}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="name_en">الاسم بالإنجليزية</Label>
              <Input
                id="name_en"
                value={formData.name_en}
                onChange={(e) => setFormData((p) => ({ ...p, name_en: e.target.value }))}
                placeholder="Store Name"
                dir="ltr"
              />
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">رقم الهاتف</Label>
              <PhoneInput
                value={formData.phone}
                onChange={(val) => setFormData((p) => ({ ...p, phone: val }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">البريد الإلكتروني</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                placeholder="info@store.com"
                dir="ltr"
              />
              {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact_person">اسم جهة الاتصال</Label>
            <Input
              id="contact_person"
              value={formData.contact_person}
              onChange={(e) => setFormData((p) => ({ ...p, contact_person: e.target.value }))}
              placeholder="اسم المسؤول"
            />
          </div>

          {/* Country & Delivery Mode */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>الدولة</Label>
              <Select
                value={formData.country_code}
                onValueChange={(v) => setFormData((p) => ({ ...p, country_code: v }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SD">السودان</SelectItem>
                  <SelectItem value="SA">السعودية</SelectItem>
                  <SelectItem value="EG">مصر</SelectItem>
                  <SelectItem value="AE">الإمارات</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>وضع التوصيل</Label>
              <Select
                value={formData.delivery_mode}
                onValueChange={(v) => setFormData((p) => ({ ...p, delivery_mode: v as any }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">ذاتي</SelectItem>
                  <SelectItem value="QuBox">عبر المنصة</SelectItem>
                  <SelectItem value="hybrid">مختلط</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>إلغاء</Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 ms-2 animate-spin" />}
              إضافة الشريك
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
